/**
 * Tabs Frontend JavaScript
 * Handles tab interaction with full accessibility support
 * @class
 */
class Tabs {

	/**
	 * @type {HTMLElement}
	 */
	block = null;

	/**
	 * @type {NodeListOf<HTMLElement>}
	 */
	tabPanels = null;

	/**
	 * @type {HTMLElement|null}
	 */
	buttonsContainer = null;

	/**
	 * @type {NodeListOf<HTMLButtonElement>}
	 */
	buttons = null;

	/**
	 * @type {Map<HTMLButtonElement, Function>}
	 */
	clickHandlers = new Map();

	/**
	 * Creates an instance of Tabs
	 * @param {HTMLElement} block - The tabs block element
	 */
	constructor( block ) {

		this.block = block;
		this.tabPanels = block.querySelectorAll( '.b-tabs__panels > .b-tab' );

		// Early exit if no tabs found
		if ( !this.tabPanels.length ) {

			return;

		}

		this.buttonsContainer = block.querySelector( '.b-tabs__buttons' );
		this.init();

	}

	/**
	 * Initializes the tabs functionality
	 * @return {void}
	 */
	init = () => {

		this.generateButtons();
		this.buttons = this.block.querySelectorAll(
			'.b-tabs__buttons .b-tab__button',
		);
		this.initializeTabs();
		this.attachEventListeners();

	};

	/**
	 * Generates tab buttons from tab panels
	 * @return {void}
	 */
	generateButtons = () => {

		if ( !this.buttonsContainer || !this.tabPanels.length ) {

			return;

		}

		// Apply style classes to buttons container
		const tabsClasses = this.block.className
			.split( ' ' )
			.filter( ( cls ) => cls.startsWith( 'is-style-' ) );

		if ( tabsClasses.length > 0 ) {

			tabsClasses.forEach( ( cls ) => {

				this.buttonsContainer.classList.add( cls );

			} );

		}

		// Clear existing buttons
		this.buttonsContainer.innerHTML = '';

		// Create buttons based on tab panels
		this.tabPanels.forEach( ( panel, index ) => {

			const button = document.createElement( 'button' );
			// Generate unique IDs for each tab panel and button
			tabCounter += 1;
			const uniqueId = `tab-${tabCounter}-${index}`;
			const panelId = `tab-panel-${uniqueId}`;
			const buttonId = `tab-button-${uniqueId}`;
			const tabTitle =
				panel.getAttribute( 'data-tab-title' ) || `Tab ${index + 1}`;

			// Set IDs and ARIA attributes
			panel.id = panelId;
			panel.setAttribute( 'aria-labelledby', buttonId );

			button.id = buttonId;
			button.setAttribute( 'role', 'tab' );
			button.setAttribute( 'aria-controls', panelId );
			button.setAttribute(
				'aria-selected',
				index === 0 ? 'true' : 'false',
			);
			button.setAttribute( 'tabindex', index === 0 ? '0' : '-1' );
			button.setAttribute( 'type', 'button' );
			button.className = `b-tab__button ${index === 0 ? 'active' : ''}`;
			button.textContent = tabTitle;

			this.buttonsContainer.appendChild( button );

		} );

	};

	/**
	 * Initializes tabs - activates first tab
	 * @return {void}
	 */
	initializeTabs = () => {

		this.deactivateAll();
		this.activate( 0 );

	};

	/**
	 * Attaches all event listeners
	 * @return {void}
	 */
	attachEventListeners = () => {

		// Add click listeners to all tab buttons
		this.buttons.forEach( ( btn, i ) => {

			/**
			 * Click handler for tab button
			 * @return {void}
			 */
			const handler = () => {

				this.deactivateAll();
				this.activate( i );

			};

			this.clickHandlers.set( btn, handler );
			btn.addEventListener( 'click', handler );

		} );

		// Keyboard navigation support
		if ( this.buttonsContainer ) {

			this.buttonsContainer.addEventListener(
				'keydown',
				this.handleKeyDown,
			);

		}

	};

	/**
	 * Removes all event listeners (cleanup)
	 * @return {void}
	 */
	destroy = () => {

		// Remove click listeners
		this.clickHandlers.forEach( ( handler, btn ) => {

			btn.removeEventListener( 'click', handler );

		} );

		this.clickHandlers.clear();

		// Remove keyboard listener
		if ( this.buttonsContainer ) {

			this.buttonsContainer.removeEventListener(
				'keydown',
				this.handleKeyDown,
			);

		}

	};

	/**
	 * Deactivates all tabs and panels
	 * @return {void}
	 */
	deactivateAll = () => {

		this.buttons.forEach( ( btn ) => {

			btn.classList.remove( 'active' );
			btn.setAttribute( 'aria-selected', 'false' );
			btn.setAttribute( 'tabindex', '-1' );

		} );

		this.tabPanels.forEach( ( panel ) => {

			panel.classList.remove( 'active' );
			panel.hidden = true;

		} );

	};

	/**
	 * Activates tab and panel by index
	 * @param {number} index - Index of tab to activate
	 * @return {void}
	 */
	activate = ( index ) => {

		if ( index < 0 || index >= this.buttons.length ) {

			return;

		}

		if ( this.buttons[index] ) {

			this.buttons[index].classList.add( 'active' );
			this.buttons[index].setAttribute( 'aria-selected', 'true' );
			this.buttons[index].setAttribute( 'tabindex', '0' );

		}

		if ( this.tabPanels[index] ) {

			this.tabPanels[index].classList.add( 'active' );
			this.tabPanels[index].hidden = false;

		}

	};

	/**
	 * Moves focus to the tab (without activating panel)
	 * @param {number} index - Index of tab to focus
	 * @return {void}
	 */
	focusTab = ( index ) => {

		if ( index < 0 || index >= this.buttons.length ) {

			return;

		}

		this.buttons.forEach( ( btn, i ) => {

			btn.setAttribute( 'tabindex', i === index ? '0' : '-1' );

		} );

		if ( this.buttons[index] ) {

			this.buttons[index].focus();

		}

	};

	/**
	 * Handles keyboard navigation
	 * @param {KeyboardEvent} event - Keyboard event
	 * @return {void}
	 */
	handleKeyDown = ( event ) => {

		const focusedEl = this.buttonsContainer.ownerDocument.activeElement;
		const currentIndex = Array.from( this.buttons ).indexOf( focusedEl );

		if ( currentIndex === -1 ) {

			return;

		}

		let newIndex = currentIndex;
		let shouldPrevent = false;

		// Arrow Right: Move to next tab
		if ( event.key === 'ArrowRight' ) {

			shouldPrevent = true;
			newIndex = ( currentIndex + 1 ) % this.buttons.length;
			this.focusTab( newIndex );

		}

		// Arrow Left: Move to previous tab
		if ( event.key === 'ArrowLeft' ) {

			shouldPrevent = true;
			newIndex =
				( currentIndex - 1 + this.buttons.length ) % this.buttons.length;
			this.focusTab( newIndex );

		}

		// Home: Move to first tab
		if ( event.key === 'Home' ) {

			shouldPrevent = true;
			this.focusTab( 0 );

		}

		// End: Move to last tab
		if ( event.key === 'End' ) {

			shouldPrevent = true;
			this.focusTab( this.buttons.length - 1 );

		}

		// Enter or Space: Activate focused tab
		if ( event.key === 'Enter' || event.key === ' ' ) {

			shouldPrevent = true;

			if ( currentIndex !== -1 ) {

				this.deactivateAll();
				this.activate( currentIndex );

			}

		}

		if ( shouldPrevent ) {

			event.preventDefault();

		}

	};

}

/**
 * Counter for unique IDs
 * @type {number}
 */
let tabCounter = 0;

// Initialize all tabs blocks on the page
document.querySelectorAll( '.b-tabs' ).forEach( ( block ) => {

	new Tabs( block );

} );
